function getAddressComponent(result: google.maps.GeocoderResult, type: string): string | undefined {
  return result.address_components.find((part) => part.types.includes(type))?.long_name;
}

/**
 * Countries where the province is the city people name.
 *
 * In Vietnam, Đà Nẵng is an administrative_area_level_1 and the locality is a
 * district, so the province reads right. This is where the rule came from.
 */
const PROVINCE_IS_THE_CITY = ['VN'];

/**
 * Resolve city label from a single geocoder result.
 *
 * The locality comes first. Until 25 October 2026 the province came first
 * everywhere, which in France is the region: the Explore header of the 2.2.11
 * recette read « Auvergne-Rhône-Alpes » for a tester standing in Annecy.
 */
export function resolveCityFromGeocodeResult(result: google.maps.GeocoderResult): string | undefined {
  const adminLevel1 = getAddressComponent(result, 'administrative_area_level_1');
  const country = result.address_components.find((part) => part.types.includes('country'))?.short_name;

  if (adminLevel1 && country && PROVINCE_IS_THE_CITY.includes(country)) {
    return adminLevel1;
  }

  const locality = getAddressComponent(result, 'locality');

  if (locality) {
    return locality;
  }

  return adminLevel1 ?? getAddressComponent(result, 'administrative_area_level_2');
}

/**
 * Resolve a city label from geocoder results.
 */
export function resolveCityFromGeocodeResults(results: google.maps.GeocoderResult[]): string | undefined {
  if (!results.length) {
    return undefined;
  }

  for (const result of results) {
    const city = resolveCityFromGeocodeResult(result);

    if (city) {
      return city;
    }
  }

  return results[0]?.formatted_address?.split(',')[0]?.trim();
}

export function resolveCountryFromGeocodeResults(results: google.maps.GeocoderResult[]): string | undefined {
  for (const result of results) {
    const country = getAddressComponent(result, 'country');

    if (country) {
      return country;
    }
  }

  return undefined;
}

export function resolveRegionFromGeocodeResults(results: google.maps.GeocoderResult[]): string | undefined {
  for (const result of results) {
    const region = getAddressComponent(result, 'administrative_area_level_2');

    if (region) {
      return region;
    }
  }

  return undefined;
}
